import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminProductsApi } from '../api';
import { useTelegram } from '../hooks/useTelegram';
import { Loading } from '../components/Loading';
import type { Category } from '../types';
import './AdminProductEditPage.css';

export const AdminProductCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { tg } = useTelegram();

  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdProductId, setCreatedProductId] = useState<number | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<number>(0);
  const [price, setPrice] = useState('');
  const [weight, setWeight] = useState('');
  const [pieces, setPieces] = useState('');
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [fat, setFat] = useState('');
  const [carbohydrates, setCarbohydrates] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);

  // Image management
  const [newImageFile, setNewImageFile] = useState<File | null>(null);
  const [newImagePreview, setNewImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

   
  useEffect(() => {
    // Show back button
    if (tg?.BackButton) {
      tg.BackButton.show();
      tg.BackButton.onClick(handleBack);
    }

    return () => {
      if (tg?.BackButton) {
        tg.BackButton.hide();
        tg.BackButton.offClick(handleBack);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tg]);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const categoriesData = await adminProductsApi.getCategories();
      setCategories(categoriesData.categories || []);

      // Set first category as default
      if (categoriesData.categories && categoriesData.categories.length > 0) {
        setCategoryId(categoriesData.categories[0].id);
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
      setError(err instanceof Error ? err.message : 'Не удалось загрузить категории');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/products');
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !createdProductId) return;

    const filesArray = Array.from(files);
    e.target.value = ''; // Reset input immediately

    try {
      setUploadingGallery(true);

      for (const file of filesArray) {
        try {
          // Upload image
          const uploadResponse = await adminProductsApi.uploadImage(createdProductId, file);

          // Add to gallery (product already has images, so get count from server)
          const productData = await adminProductsApi.getProduct(createdProductId);
          const sortOrder = (productData.images?.length || 0) + 1;

          await adminProductsApi.addProductImage(
            createdProductId,
            uploadResponse.image_url,
            sortOrder,
            false
          );
        } catch (err) {
          console.error('Error uploading file:', file.name, err);
          tg?.showAlert(`Ошибка загрузки ${file.name}`);
        }
      }

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('success');
      }
    } catch (err: unknown) {
      tg?.showAlert(err instanceof Error ? err.message : 'Ошибка загрузки изображений');
    } finally {
      setUploadingGallery(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!name.trim()) {
      tg?.showAlert('Введите название товара');
      return;
    }
    if (!description.trim()) {
      tg?.showAlert('Введите описание товара');
      return;
    }
    if (!categoryId) {
      tg?.showAlert('Выберите категорию');
      return;
    }
    if (!price || parseFloat(price) <= 0) {
      tg?.showAlert('Введите корректную цену');
      return;
    }
    if (!weight && !pieces) {
      tg?.showAlert('Укажите вес или количество штук');
      return;
    }

    try {
      setSaving(true);

      let imageUrl: string | undefined = undefined;

      // Upload main image if selected
      if (newImageFile) {
        setUploadingImage(true);
        // Create temporary product first to get ID for image upload
        const tempProduct = await adminProductsApi.createProduct({
          name: name.trim(),
          description: description.trim(),
          category_id: categoryId,
          price: parseFloat(price),
          weight: weight ? parseInt(weight) : null,
          pieces: pieces ? parseInt(pieces) : null,
          calories: calories ? parseFloat(calories) : null,
          protein: protein ? parseFloat(protein) : null,
          fat: fat ? parseFloat(fat) : null,
          carbohydrates: carbohydrates ? parseFloat(carbohydrates) : null,
          is_available: isAvailable,
        });

        setCreatedProductId(tempProduct.id);

        // Upload image
        const uploadResponse = await adminProductsApi.uploadImage(tempProduct.id, newImageFile);
        imageUrl = uploadResponse.image_url;
        setUploadingImage(false);

        // Update product with image URL
        await adminProductsApi.updateProduct(tempProduct.id, {
          image_url: imageUrl,
        });
      } else {
        // Create product without image
        const newProduct = await adminProductsApi.createProduct({
          name: name.trim(),
          description: description.trim(),
          category_id: categoryId,
          price: parseFloat(price),
          weight: weight ? parseInt(weight) : null,
          pieces: pieces ? parseInt(pieces) : null,
          calories: calories ? parseFloat(calories) : null,
          protein: protein ? parseFloat(protein) : null,
          fat: fat ? parseFloat(fat) : null,
          carbohydrates: carbohydrates ? parseFloat(carbohydrates) : null,
          is_available: isAvailable,
          image_url: imageUrl,
        });

        setCreatedProductId(newProduct.id);
      }

      tg?.showAlert('Товар успешно создан');

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('success');
      }

      // Navigate to products list
      navigate('/admin/products', { replace: true });
    } catch (err: unknown) {
      tg?.showAlert(err instanceof Error ? err.message : 'Ошибка создания товара');

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setSaving(false);
      setUploadingImage(false);
    }
  };

  if (loading) {
    return (
      <div className="page admin-product-edit-page">
        <div className="admin-product-edit-header">
          <h1 className="admin-product-edit-title">Создание товара</h1>
        </div>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page admin-product-edit-page">
        <div className="admin-product-edit-header">
          <h1 className="admin-product-edit-title">Создание товара</h1>
        </div>
        <div className="admin-product-edit-error">
          <p className="admin-product-edit-error-message">{error}</p>
          <button className="admin-product-edit-retry-btn" onClick={loadCategories}>
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page admin-product-edit-page">
      <div className="admin-product-edit-header">
        <h1 className="admin-product-edit-title">Создание товара</h1>
      </div>

      <div className="admin-product-edit-content">
        <div className="admin-product-edit-form">
          {/* Name */}
          <div className="form-group">
            <label className="form-label">Название *</label>
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Введите название товара"
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">Описание *</label>
            <textarea
              className="form-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Введите описание товара"
              rows={4}
            />
          </div>

          {/* Category */}
          <div className="form-group">
            <label className="form-label">Категория *</label>
            <select
              className="form-select"
              value={categoryId}
              onChange={(e) => setCategoryId(parseInt(e.target.value))}
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Price */}
          <div className="form-group">
            <label className="form-label">Цена (₽) *</label>
            <input
              type="number"
              className="form-input"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0"
              min="0"
              step="0.01"
            />
          </div>

          {/* Weight and Pieces */}
          <span className="form-hint">Укажите вес или количество штук (или оба параметра)</span>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Вес (г)</label>
              <input
                type="number"
                className="form-input"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="0"
                min="0"
                step="1"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Количество штук</label>
              <input
                type="number"
                className="form-input"
                value={pieces}
                onChange={(e) => setPieces(e.target.value)}
                placeholder="0"
                min="0"
                step="1"
              />
            </div>
          </div>

          {/* Nutritional Info */}
          <div className="form-section-title">Пищевая ценность (на 100г)</div>

          <div className="form-group">
            <label className="form-label">Калории (ккал)</label>
            <input
              type="number"
              className="form-input"
              value={calories}
              onChange={(e) => setCalories(e.target.value)}
              placeholder="0"
              min="0"
              step="0.1"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Белки (г)</label>
              <input
                type="number"
                className="form-input"
                value={protein}
                onChange={(e) => setProtein(e.target.value)}
                placeholder="0"
                min="0"
                step="0.1"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Жиры (г)</label>
              <input
                type="number"
                className="form-input"
                value={fat}
                onChange={(e) => setFat(e.target.value)}
                placeholder="0"
                min="0"
                step="0.1"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Углеводы (г)</label>
              <input
                type="number"
                className="form-input"
                value={carbohydrates}
                onChange={(e) => setCarbohydrates(e.target.value)}
                placeholder="0"
                min="0"
                step="0.1"
              />
            </div>
          </div>

          {/* Availability */}
          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={isAvailable}
                onChange={(e) => setIsAvailable(e.target.checked)}
              />
              <span className="checkbox-label">Доступен для заказа</span>
            </label>
          </div>

          {/* Main Image */}
          <div className="form-group">
            <label className="form-label">Основное изображение</label>
            <div className="image-upload-section">
              <div className="main-image-container">
                {newImagePreview ? (
                  <div className="image-preview main">
                    <img src={newImagePreview} alt="Preview" />
                    <button
                      className="image-remove-btn"
                      onClick={() => {
                        setNewImageFile(null);
                        setNewImagePreview(null);
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <label className="image-upload-placeholder">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      style={{ display: 'none' }}
                    />
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                    <span>Загрузить изображение</span>
                  </label>
                )}
                {newImagePreview && (
                  <label className="image-change-btn">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      style={{ display: 'none' }}
                    />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    Изменить
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* Gallery - only show after product is created */}
          {createdProductId && (
            <div className="form-group">
              <label className="form-label">Галерея изображений</label>
              <div className="gallery-section">
                <div className="gallery-grid">
                  <label className={`gallery-add-item ${uploadingGallery ? 'uploading' : ''}`}>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleGalleryImageSelect}
                      style={{ display: 'none' }}
                      disabled={uploadingGallery}
                    />
                    {uploadingGallery ? (
                      <>
                        <svg className="spinner" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                        </svg>
                        <span>Загрузка...</span>
                      </>
                    ) : (
                      <>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="12" y1="5" x2="12" y2="19"/>
                          <line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                        <span>Добавить фото</span>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <button
            className="admin-product-edit-save-btn"
            onClick={handleSave}
            disabled={saving || uploadingImage}
          >
            {saving ? 'Сохранение...' : 'Создать товар'}
          </button>
        </div>
      </div>
    </div>
  );
};
